const parseJson = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback
  } catch (_error) {
    return fallback
  }
}

const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;")

const placementPayload = (placement) => ({
  integration_id: placement.integration_id,
  widget_key: placement.widget.key,
  column_span: placement.widget.column_span,
  row_span: placement.widget.row_span,
  config: placement.config || {}
})

const buildDefaultPayload = (widget, integrationName) => ({
  title: widget?.label || "Unavailable Widget",
  subtitle: integrationName,
  blocks: [
    {
      type: "highlight",
      title: "Preview after save",
      value: widget?.description || "Save the dashboard to load widget data."
    }
  ]
})

const configSchemaKey = (integrationId, widgetKey) => `${integrationId}:${widgetKey}`

const normalizeWidget = (widget, fallback = {}) => ({
  key: widget?.key || fallback.key || "unknown",
  label: widget?.label || fallback.label || "Unavailable Widget",
  description: widget?.description || fallback.description || "This widget definition could not be loaded.",
  category: widget?.category || fallback.category || "Unavailable",
  object_widget: Boolean(widget?.object_widget ?? fallback.object_widget),
  column_span: Number(widget?.column_span || fallback.column_span || 1),
  row_span: Number(widget?.row_span || fallback.row_span || 1),
  config_schema: widget?.config_schema ?? fallback.config_schema ?? null,
  preset_config: widget?.preset_config || fallback.preset_config || {},
  locked_config: Boolean(widget?.locked_config ?? fallback.locked_config),
  source_label: widget?.source_label || fallback.source_label || "",
  schema_loading: false
})

const normalizeWidgetGroup = (group) => ({
  integration_id: group?.integration_id || "",
  integration_name: group?.integration_name || "Unavailable Integration",
  integration_key: group?.integration_key || "unavailable",
  widgets: Array.isArray(group?.widgets) ? group.widgets : []
})

const flattenWidgets = (availableWidgets) => {
  const lookup = new Map()

  Array.from(availableWidgets || []).map(normalizeWidgetGroup).forEach((group) => {
    Array.from(group.widgets || []).forEach((widget) => {
      lookup.set(`${group.integration_id}:${widget.palette_key || widget.key}`, {
        integration_id: group.integration_id,
        integration_name: group.integration_name,
        integration_key: group.integration_key,
        widget
      })
    })
  })

  return lookup
}

const renderBlock = (block) => {
  const type = block?.type

  if (type === "stats") {
    return `
      <div class="dashboard-widget-stats">
        ${Array(block.items || []).map((item) => `
          <div class="dashboard-widget-stat" data-tone="${escapeHtml(item.tone || "")}">
            <span>${escapeHtml(item.label)}</span>
            <strong>${escapeHtml(item.value)}</strong>
          </div>
        `).join("")}
      </div>
    `
  }

  if (type === "rows" || type === "section") {
    return `
      ${(block.title ? `<p class="site-stat-label">${escapeHtml(block.title)}</p>` : "")}
      <dl class="dashboard-widget-rows">
        ${Array(block.items || []).map((item) => `
          <div class="dashboard-widget-row" data-tone="${escapeHtml(item.tone || "")}">
            <dt>${escapeHtml(item.label)}</dt>
            <dd>${escapeHtml(item.value)}</dd>
          </div>
        `).join("")}
      </dl>
    `
  }

  if (type === "highlight") {
    return `
      <div class="dashboard-widget-highlight" data-tone="${escapeHtml(block.tone || "")}">
        <span>${escapeHtml(block.title)}</span>
        <strong>${escapeHtml(block.value)}</strong>
      </div>
    `
  }

  if (Array.isArray(block?.items)) {
    return `
      ${(block.title ? `<p class="site-stat-label">${escapeHtml(block.title)}</p>` : "")}
      <dl class="dashboard-widget-rows">
        ${Array(block.items || []).map((item) => `
          <div class="dashboard-widget-row" data-tone="${escapeHtml(item.tone || "")}">
            <dt>${escapeHtml(item.label || item.title || "Value")}</dt>
            <dd>${escapeHtml(item.value ?? item.meta ?? "")}</dd>
          </div>
        `).join("")}
      </dl>
    `
  }

  if (block && (block.label || block.title || block.value)) {
    return `
      <div class="dashboard-widget-highlight" data-tone="${escapeHtml(block.tone || "")}">
        <span>${escapeHtml(block.title || block.label || "Value")}</span>
        <strong>${escapeHtml(block.value ?? block.meta ?? "")}</strong>
      </div>
    `
  }

  return ""
}

const renderPayloadFallback = (payload) => {
  const scalarEntries = Object.entries(payload || {}).filter(([key, value]) => {
    if (["title", "subtitle", "blocks"].includes(String(key))) return false
    if (value === null || value === undefined || value === "") return false
    return typeof value !== "object"
  })

  if (scalarEntries.length) {
    return `
      <dl class="dashboard-widget-rows">
        ${scalarEntries.map(([key, value]) => `
          <div class="dashboard-widget-row">
            <dt>${escapeHtml(key.replaceAll("_", " "))}</dt>
            <dd>${escapeHtml(value)}</dd>
          </div>
        `).join("")}
      </dl>
    `
  }

  return `
    <div class="dashboard-widget-highlight">
      <span>No live details</span>
      <strong>Widget returned no displayable blocks</strong>
    </div>
  `
}

const renderSchemaField = (key, field, value) => {
  const type = String(field?.type || "string")

  if (type === "message") {
    return `
      <div class="dashboard-widget-form-field dashboard-widget-form-message" data-tone="${escapeHtml(field.tone || "")}">
        <span class="plan-mapping-label">${escapeHtml(field.label || key)}</span>
        <p>${escapeHtml(field.value || "")}</p>
      </div>
    `
  }

  if (type === "select") {
    return `
      <label class="dashboard-widget-form-field">
        <span class="plan-mapping-label">${escapeHtml(field.label || key)}</span>
        <select class="plan-mapping-input" data-dashboard-config-key="${escapeHtml(key)}">
          <option value="">Select</option>
          ${Array(field.options || []).map((option) => `
            <option value="${escapeHtml(option.value)}" ${String(option.value) === String(value || "") ? "selected" : ""}>
              ${escapeHtml(option.label)}
            </option>
          `).join("")}
        </select>
        ${field.help ? `<p class="site-hero-subtitle">${escapeHtml(field.help)}</p>` : ""}
      </label>
    `
  }

  return `
    <label class="dashboard-widget-form-field">
      <span class="plan-mapping-label">${escapeHtml(field.label || key)}</span>
      <input type="text" class="plan-mapping-input" value="${escapeHtml(value || "")}" data-dashboard-config-key="${escapeHtml(key)}">
      ${field.help ? `<p class="site-hero-subtitle">${escapeHtml(field.help)}</p>` : ""}
    </label>
  `
}

const createDragGhost = (label, x, y) => {
  const ghost = document.createElement("div")
  ghost.className = "dashboard-drag-ghost"
  ghost.textContent = label
  ghost.style.left = `${x + 14}px`
  ghost.style.top = `${y + 14}px`
  document.body.appendChild(ghost)
  return ghost
}

export const mountDashboardEditors = () => {
  document.querySelectorAll("[data-dashboard-editor]").forEach((container) => {
    if (container.dataset.mounted === "true") return
    container.dataset.mounted = "true"

    const payload = parseJson(container.dataset.dashboardEditorPayload, { available_widgets: [], placements: [] })
    const input = container.querySelector("[data-dashboard-editor-input]") || container.closest("form")?.querySelector("[data-dashboard-editor-input]")
    const paletteEl = container.querySelector("[data-dashboard-editor-palette]")
    const boardEl = container.querySelector("[data-dashboard-editor-board]")

    try {
      const availableWidgets = Array.from(payload.available_widgets || []).map(normalizeWidgetGroup)
      const schemaUrl = container.dataset.dashboardSchemaUrl
      const widgetLookup = flattenWidgets(availableWidgets)
      const schemaCache = new Map()
      const schemaRequests = new Map()

      let placements = Array.from(payload.placements || []).filter(Boolean).map((placement, index) => {
        const widget = normalizeWidget(placement.widget, {
          key: placement.widget_key,
          label: placement.widget_key ? String(placement.widget_key).replaceAll(".", " ").replace(/\b\w/g, (char) => char.toUpperCase()) : undefined
        })

        return {
          id: placement.id || `placement-${index}-${Date.now()}`,
          integration_id: placement.integration_id,
          integration_name: placement.integration_name,
          integration_key: placement.integration_key,
          widget,
          config: placement.config || {},
          payload: placement.payload || buildDefaultPayload(widget, placement.integration_name)
        }
      }).filter((placement) => placement.integration_id && placement.widget?.key)

      const syncInput = () => {
        if (!input) return
        input.value = JSON.stringify(placements.map(placementPayload))
      }

      const renderPalette = () => {
        if (!paletteEl) return

        if (!availableWidgets.length) {
          paletteEl.innerHTML = `<p class="site-hero-subtitle">No assigned integrations are available for this ${escapeHtml(container.dataset.dashboardTargetType || "target")}.</p>`
          return
        }

        paletteEl.innerHTML = availableWidgets.map((group) => `
          <section class="dashboard-palette-group">
            <div class="dashboard-palette-group-head">
              <p class="site-stat-label">${escapeHtml(group.integration_key)}</p>
              <h3>${escapeHtml(group.integration_name)}</h3>
            </div>
            <div class="dashboard-palette-list">
              ${group.widgets.map((widget) => `
                <article class="dashboard-palette-item" data-dashboard-palette-widget="${escapeHtml(`${group.integration_id}:${widget.palette_key || widget.key}`)}">
                  <div>
                    <h4>${escapeHtml(widget.label)}</h4>
                    ${widget.source_label ? `<p class="site-stat-label">${escapeHtml(widget.source_label)}</p>` : ""}
                    ${widget.description ? `<p class="site-hero-subtitle">${escapeHtml(widget.description)}</p>` : ""}
                  </div>
                  <div class="dashboard-palette-item-actions">
                    <span class="dashboard-widget-drag-handle" title="Drag to board">::</span>
                    <button type="button" class="btn btn-ghost" data-dashboard-add-widget="${escapeHtml(`${group.integration_id}:${widget.palette_key || widget.key}`)}">Add</button>
                  </div>
                </article>
              `).join("")}
            </div>
          </section>
        `).join("")
      }

      const renderBoard = () => {
        if (!boardEl) return

      if (!placements.length) {
        boardEl.innerHTML = `<div class="dashboard-editor-empty" data-dashboard-drop-zone><span>Drag widgets here</span></div>`
        syncInput()
        return
      }

        boardEl.innerHTML = `
        <div class="dashboard-grid dashboard-grid-edit">
          ${placements.map((placement, index) => {
            const schema = placement.widget.config_schema || {}
            const configFields = Object.entries(schema).map(([key, field]) => renderSchemaField(key, field, placement.config?.[key])).join("")
            const schemaLoadingMarkup = placement.widget.schema_loading ? `
              <div class="dashboard-widget-form">
                <div class="dashboard-skeleton-line dashboard-skeleton-line-short"></div>
                <div class="dashboard-skeleton-panel"></div>
              </div>
            ` : ""
            const configMarkup = schema && Object.keys(schema).length ? `<div class="dashboard-widget-form">${configFields}</div>` : ""

            return `
              <article
                class="dashboard-widget-card dashboard-widget-card-edit"
                style="grid-column: span ${placement.widget.column_span}; grid-row: span ${placement.widget.row_span};"
                data-dashboard-placement-id="${escapeHtml(placement.id)}"
                data-dashboard-placement-index="${index}"
                data-dashboard-drop-zone>
                <header class="dashboard-widget-head">
                  <div>
                    <p class="site-stat-label">${escapeHtml(placement.integration_name)}</p>
                    <h3>${escapeHtml(placement.widget.label)}</h3>
                    ${placement.widget.source_label ? `<p class="site-hero-subtitle">${escapeHtml(placement.widget.source_label)}</p>` : ""}
                  </div>
                  <div class="dashboard-widget-controls">
                    <span class="dashboard-widget-drag-handle" title="Drag to reorder">::</span>
                    <button type="button" class="btn btn-ghost" data-dashboard-remove="${escapeHtml(placement.id)}">Remove</button>
                  </div>
                </header>

                <div class="dashboard-widget-sizing">
                  <label class="dashboard-widget-form-field">
                    <span class="plan-mapping-label">Width</span>
                    <select class="plan-mapping-input" data-dashboard-size-key="column_span" data-dashboard-placement="${escapeHtml(placement.id)}">
                      ${[1, 2, 3, 4].map((value) => `<option value="${value}" ${value === placement.widget.column_span ? "selected" : ""}>${value}</option>`).join("")}
                    </select>
                  </label>
                  <label class="dashboard-widget-form-field">
                    <span class="plan-mapping-label">Height</span>
                    <select class="plan-mapping-input" data-dashboard-size-key="row_span" data-dashboard-placement="${escapeHtml(placement.id)}">
                      ${[1, 2, 3, 4].map((value) => `<option value="${value}" ${value === placement.widget.row_span ? "selected" : ""}>${value}</option>`).join("")}
                    </select>
                  </label>
                </div>

                ${schemaLoadingMarkup}
                ${configMarkup}

                <div class="dashboard-widget-body">
                  ${Array(placement.payload?.blocks || []).map(renderBlock).join("")}
                </div>
              </article>
            `
          }).join("")}
          <div class="dashboard-editor-slot" data-dashboard-drop-zone data-dashboard-placement-index="${placements.length}">
            <span>Drop at end</span>
          </div>
        </div>
      `

      syncInput()
      }

      const requestSchema = async (integrationId, widgetKey) => {
        const key = configSchemaKey(integrationId, widgetKey)
        if (schemaCache.has(key)) return schemaCache.get(key)
        if (schemaRequests.has(key)) return schemaRequests.get(key)
        if (!schemaUrl) return null

      const url = new URL(schemaUrl, window.location.origin)
      url.searchParams.set("integration_id", integrationId)
      url.searchParams.set("widget_key", widgetKey)

      const request = fetch(url.toString(), {
        headers: {
          Accept: "application/json"
        },
        credentials: "same-origin"
      })
        .then(async (response) => {
          const data = await response.json().catch(() => ({}))
          if (!response.ok) {
            throw new Error(data.error || "Configuration unavailable")
          }

          const schema = data.schema || {}
          schemaCache.set(key, schema)
          return schema
        })
        .catch((error) => {
          const schema = {
            _error: {
              type: "message",
              tone: "error",
              label: "Configuration unavailable",
              value: error.message
            }
          }
          schemaCache.set(key, schema)
          return schema
        })
        .finally(() => {
          schemaRequests.delete(key)
        })

      schemaRequests.set(key, request)
      return request
      }

      const ensurePlacementSchema = async (placementId) => {
        const placement = placements.find((entry) => entry.id === placementId)
        if (!placement) return
        if (placement.widget.config_schema !== null && placement.widget.config_schema !== undefined) return
        if (placement.widget.locked_config) {
          placement.widget.config_schema = {}
          placement.widget.schema_loading = false
          renderBoard()
          return
        }
        if (!placement.integration_id || !placement.widget?.key || placement.widget.key === "unknown") {
          placement.widget.config_schema = {}
          placement.widget.schema_loading = false
          renderBoard()
          return
        }

        placement.widget.schema_loading = true
        renderBoard()

        const schema = await requestSchema(placement.integration_id, placement.widget.key)
        const refreshedPlacement = placements.find((entry) => entry.id === placementId)
        if (!refreshedPlacement) return

        refreshedPlacement.widget.config_schema = schema || {}
        refreshedPlacement.widget.schema_loading = false
        renderBoard()
      }

      const hydrateVisibleSchemas = () => {
        placements.forEach((placement) => {
          void ensurePlacementSchema(placement.id)
        })
      }

      const addPlacement = (lookupKey, index = placements.length) => {
        const definition = widgetLookup.get(lookupKey)
        if (!definition) return

        const widget = normalizeWidget(definition.widget)

        placements.splice(index, 0, {
          id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          integration_id: definition.integration_id,
          integration_name: definition.integration_name,
          integration_key: definition.integration_key,
          widget,
          config: { ...(widget.preset_config || {}) },
          payload: buildDefaultPayload(widget, definition.integration_name)
        })

        renderBoard()
        void ensurePlacementSchema(placements[index]?.id)
      }

      let pointerDrag = null

      renderPalette()
      renderBoard()
      hydrateVisibleSchemas()
      container.dataset.dashboardReady = "true"

      paletteEl?.addEventListener("click", (event) => {
        const button = event.target.closest("[data-dashboard-add-widget]")
        if (!button) return
        addPlacement(button.dataset.dashboardAddWidget)
      })

      const clearDropTargets = () => {
        boardEl?.querySelectorAll(".is-drop-target").forEach((element) => element.classList.remove("is-drop-target"))
      }

      const updateDropTarget = (clientX, clientY) => {
        clearDropTargets()
        const target = document.elementFromPoint(clientX, clientY)?.closest("[data-dashboard-drop-zone]")
        if (!target || !boardEl?.contains(target)) return null

        target.classList.add("is-drop-target")
        return target
      }

      const finishPointerDrag = (clientX, clientY) => {
        if (!pointerDrag) return

      const dropTarget = updateDropTarget(clientX, clientY)
      const targetIndex = Number(dropTarget?.dataset.dashboardPlacementIndex || placements.length)

      if (dropTarget && pointerDrag.mode === "palette") {
        addPlacement(pointerDrag.lookupKey, Number.isNaN(targetIndex) ? placements.length : targetIndex)
      } else if (dropTarget && pointerDrag.mode === "placement") {
        const fromIndex = placements.findIndex((placement) => placement.id === pointerDrag.placementId)
        if (fromIndex >= 0) {
          const [moved] = placements.splice(fromIndex, 1)
          let insertIndex = Number.isNaN(targetIndex) ? placements.length : targetIndex
          if (fromIndex < insertIndex) insertIndex -= 1
          placements.splice(insertIndex, 0, moved)
          renderBoard()
        }
      }

        pointerDrag.ghost?.remove()
        pointerDrag = null
        clearDropTargets()
      }

      paletteEl?.addEventListener("pointerdown", (event) => {
      const item = event.target.closest("[data-dashboard-palette-widget]")
      if (!item || event.target.closest("[data-dashboard-add-widget]")) return

      const lookupKey = item.dataset.dashboardPaletteWidget
      const label = item.querySelector("h4")?.textContent?.trim() || "Widget"
      pointerDrag = {
        mode: "palette",
        lookupKey,
        ghost: createDragGhost(label, event.clientX, event.clientY)
      }
      event.preventDefault()
      })

      boardEl?.addEventListener("pointerdown", (event) => {
      if (!event.target.closest(".dashboard-widget-drag-handle")) return

      const card = event.target.closest("[data-dashboard-placement-id]")
      if (!card) return

      const label = card.querySelector("h3")?.textContent?.trim() || "Widget"
      pointerDrag = {
        mode: "placement",
        placementId: card.dataset.dashboardPlacementId,
        ghost: createDragGhost(label, event.clientX, event.clientY)
      }
      event.preventDefault()
      })

      document.addEventListener("pointermove", (event) => {
      if (!pointerDrag) return

      if (pointerDrag.ghost) {
        pointerDrag.ghost.style.left = `${event.clientX + 14}px`
        pointerDrag.ghost.style.top = `${event.clientY + 14}px`
      }

      updateDropTarget(event.clientX, event.clientY)
      })

      document.addEventListener("pointerup", (event) => finishPointerDrag(event.clientX, event.clientY))

      boardEl?.addEventListener("click", (event) => {
      const removeButton = event.target.closest("[data-dashboard-remove]")
      if (!removeButton) return

      placements = placements.filter((placement) => placement.id !== removeButton.dataset.dashboardRemove)
      renderBoard()
      })

      boardEl?.addEventListener("change", (event) => {
      const configInput = event.target.closest("[data-dashboard-config-key]")
      if (configInput) {
        const card = event.target.closest("[data-dashboard-placement-id]")
        const placement = placements.find((entry) => entry.id === card?.dataset.dashboardPlacementId)
        if (!placement) return

        placement.config = {
          ...(placement.config || {}),
          [configInput.dataset.dashboardConfigKey]: configInput.value
        }
        syncInput()
        return
      }

      const sizeInput = event.target.closest("[data-dashboard-size-key]")
      if (!sizeInput) return

      const placement = placements.find((entry) => entry.id === sizeInput.dataset.dashboardPlacement)
      if (!placement) return

      placement.widget[sizeInput.dataset.dashboardSizeKey] = Number(sizeInput.value || 1)
      renderBoard()
      })
    } catch (error) {
      console.error("Dashboard editor failed to mount", error)
      if (paletteEl) {
        paletteEl.innerHTML = `<div class="plan-mapping-feedback" data-tone="error">Dashboard editor failed to load: ${escapeHtml(error.message || "Unknown error")}</div>`
      }
      if (boardEl) {
        boardEl.innerHTML = `<div class="plan-mapping-feedback" data-tone="error">Reload the page after the editor assets finish updating.</div>`
      }
      container.dataset.dashboardReady = "true"
    }
  })
}

export const teardownDashboardEditors = () => {
  document.querySelectorAll("[data-dashboard-editor]").forEach((container) => {
    delete container.dataset.mounted
    container.dataset.dashboardReady = "false"
  })

  document.querySelectorAll("[data-dashboard-view]").forEach((container) => {
    delete container.dataset.mounted
  })
}

export const mountDashboardViews = () => {
  document.querySelectorAll("[data-dashboard-view]").forEach((container) => {
    if (container.dataset.mounted === "true") return
    container.dataset.mounted = "true"

    try {
      const liveUrl = container.dataset.dashboardLiveUrl
      const cards = Array.from(container.querySelectorAll("[data-dashboard-view-placement-id]"))

      const renderPlacement = (card, placement) => {
        if (!card) return

        const titleEl = card.querySelector("[data-dashboard-view-title]")
        const subtitleEl = card.querySelector("[data-dashboard-view-subtitle]")
        const bodyEl = card.querySelector("[data-dashboard-view-body]")

        if (titleEl) {
          titleEl.textContent = placement.title || placement.payload?.title || placement.widget?.label || titleEl.textContent
        }

        if (bodyEl) {
          if (placement.body_html) {
            bodyEl.innerHTML = placement.body_html
          } else {
            const blocksHtml = Array(placement.payload?.blocks || []).map(renderBlock).join("")
            bodyEl.innerHTML = blocksHtml.trim().length > 0 ? blocksHtml : renderPayloadFallback(placement.payload || {})
          }
        }

        const subtitle = placement.subtitle || placement.payload?.subtitle || placement.widget?.source_label || ""
        if (subtitleEl) {
          subtitleEl.textContent = subtitle
          subtitleEl.hidden = subtitle.length === 0
        } else if (subtitle.length > 0 && titleEl) {
          const subtitleNode = document.createElement("p")
          subtitleNode.className = "site-hero-subtitle"
          subtitleNode.dataset.dashboardViewSubtitle = "true"
          subtitleNode.textContent = subtitle
          titleEl.insertAdjacentElement("afterend", subtitleNode)
        }

        card.classList.remove("dashboard-widget-card-loading")
      }

      const renderError = (card, message) => {
        const bodyEl = card.querySelector("[data-dashboard-view-body]")
        if (bodyEl) {
          bodyEl.innerHTML = `
            <div class="dashboard-widget-highlight" data-tone="error">
              <span>Dashboard unavailable</span>
              <strong>${escapeHtml(message)}</strong>
            </div>
          `
        }
        card.classList.remove("dashboard-widget-card-loading")
      }

      if (!liveUrl || !cards.length) return

      cards.forEach((card) => {
        const placementId = card.dataset.dashboardViewPlacementId
        if (!placementId) {
          renderError(card, "Missing placement id")
          return
        }

        const url = new URL(liveUrl, window.location.origin)
        url.searchParams.set("placement_id", placementId)

        fetch(url.toString(), {
          headers: {
            Accept: "application/json"
          },
          credentials: "same-origin"
        })
          .then(async (response) => {
            const data = await response.json().catch(() => ({}))
            if (!response.ok) {
              throw new Error(data.error || "Could not load dashboard widget")
            }

            if (!data.placement) {
              throw new Error("Dashboard widget payload was empty")
            }

            renderPlacement(card, data.placement)
          })
          .catch((error) => {
            renderError(card, error.message)
          })
      })
    } catch (error) {
      console.error("Dashboard view failed to mount", error)
      container.querySelectorAll("[data-dashboard-view-placement-id]").forEach((card) => {
        const bodyEl = card.querySelector("[data-dashboard-view-body]")
        if (bodyEl) {
          bodyEl.innerHTML = `
            <div class="dashboard-widget-highlight" data-tone="error">
              <span>Dashboard unavailable</span>
              <strong>${escapeHtml(error.message || "Dashboard failed to load")}</strong>
            </div>
          `
        }
        card.classList.remove("dashboard-widget-card-loading")
      })
    }
  })
}
